import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
  faExclamationCircle,
  faBan,
  faCreditCard,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { pushEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import type { PaymentFailureStatus } from '../../../types/account';
import './PaymentFailureWarning.css';

type WarningTone = 'critical' | 'warning' | 'info' | 'neutral';
type BootstrapVariant = 'danger' | 'warning' | 'info' | 'secondary' | 'primary';

interface WarningConfig {
  variant: BootstrapVariant;
  tone: WarningTone;
  icon: IconDefinition;
  title: string;
  urgency: string;
  buttonVariant: BootstrapVariant;
  buttonText: string;
}

interface PaymentFailureWarningProps {
  paymentStatus: PaymentFailureStatus | null;
  onUpdatePaymentClick?: () => void;
  loading?: boolean;
}

const WARNING_CONFIG: Record<string, WarningConfig> = {
  critical: {
    variant: 'danger',
    tone: 'critical',
    icon: faBan,
    title: 'Services suspended',
    urgency: 'Immediate action required',
    buttonVariant: 'danger',
    buttonText: 'Update Payment Method Now'
  },
  warning: {
    variant: 'warning',
    tone: 'warning',
    icon: faExclamationTriangle,
    title: 'Multiple payment failures',
    urgency: 'Action required',
    buttonVariant: 'warning',
    buttonText: 'Update Payment Method'
  },
  info: {
    variant: 'info',
    tone: 'info',
    icon: faInfoCircle,
    title: 'Payment failed',
    urgency: 'Grace period active',
    buttonVariant: 'primary',
    buttonText: 'Update Payment Method'
  },
  default: {
    variant: 'secondary',
    tone: 'neutral',
    icon: faExclamationCircle,
    title: 'Payment issue',
    urgency: 'Please review',
    buttonVariant: 'secondary',
    buttonText: 'Review Payment'
  }
} satisfies Record<string, WarningConfig>;

const formatCurrency = (amount?: number | null): string => {
  if (!amount || amount === 0) return '$0.00';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const PaymentFailureWarning = ({
  paymentStatus,
  onUpdatePaymentClick,
  loading = false
}: PaymentFailureWarningProps): React.ReactElement | null => {
  if (!paymentStatus || !paymentStatus.hasActiveFailures) {
    return null;
  }

  const handleUpdatePaymentClick = (): void => {
    pushEvent({
      event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
      action: 'payment_warning_clicked',
      warning_level: paymentStatus.warningLevel,
      failure_count: paymentStatus.failureCount,
      services_suspended: paymentStatus.servicesSuspended,
      total_amount_due: paymentStatus.totalAmountDue
    });

    if (onUpdatePaymentClick) {
      onUpdatePaymentClick();
    }
  };

  const config = WARNING_CONFIG[paymentStatus.warningLevel || 'default'] || WARNING_CONFIG.default;

  return (
    <Alert
      variant={config.variant}
      className={`payment-failure-warning payment-failure-tone-${config.tone}`}
    >
      <div className="payment-warning-layout">
        <div className="warning-content">
          <div className="warning-header">
            <FontAwesomeIcon
              icon={config.icon}
              className="warning-icon"
              size="lg"
            />
            <div className="warning-title-section">
              <h5 className="warning-title">{config.title}</h5>
              <div className="warning-urgency">{config.urgency}</div>
            </div>
          </div>

          <div className="warning-message">
            <p>{paymentStatus.warningMessage}</p>

            <div className="payment-details" aria-label="Payment failure details">
              <div className="payment-detail">
                <span>Failed payments</span>
                <strong>{paymentStatus.failureCount}</strong>
              </div>
              <div className="payment-detail">
                <span>Amount due</span>
                <strong>{formatCurrency(paymentStatus.totalAmountDue)}</strong>
              </div>
              {paymentStatus.gracePeriodActive && (
                <div className="payment-detail payment-detail--wide payment-detail--success">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span><strong>Grace period:</strong> Your deployments are still running</span>
                </div>
              )}
              {paymentStatus.servicesSuspended && (
                <div className="payment-detail payment-detail--wide payment-detail--danger">
                  <FontAwesomeIcon icon={faBan} />
                  <span>
                    <strong>Services suspended:</strong>{' '}
                    <span>All deployments have been stopped</span>
                    <small className="payment-detail-note">
                      Existing broker positions and open orders may still remain in your account.
                    </small>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="warning-actions">
          <Button
            type="button"
            variant={config.buttonVariant}
            size="lg"
            onClick={handleUpdatePaymentClick}
            disabled={loading}
            className="update-payment-warning-btn"
          >
            <FontAwesomeIcon icon={faCreditCard} className="me-2" />
            {config.buttonText}
          </Button>

          {config.tone === 'critical' && (
            <p className="warning-action-note">
              Update payment to restore service immediately
            </p>
          )}
        </div>
      </div>
    </Alert>
  );
};

export default PaymentFailureWarning;
